import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Navbar, Footer } from "@/components/landing";
import { ArrowLeft } from "lucide-react";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import { useLocale } from "@/hooks/useLocale";

const companyName = "Holy Moly";
const contactEmail = "support@holymolylab.com";
const lastUpdatedEn = "February 6, 2026";
const lastUpdatedFr = "6 février 2026";

export function TermsOfServicePage() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const locale = useLocale();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = locale === "fr" ? lastUpdatedFr : lastUpdatedEn;
  const accountItems = t("legal.terms.sections.accounts.items", {
    returnObjects: true,
  }) as string[];
  const paymentItems = t("legal.terms.sections.payment.items", {
    returnObjects: true,
  }) as string[];
  const useItems = t("legal.terms.sections.use.items", {
    returnObjects: true,
  }) as string[];

  return (
    <div className="relative min-h-screen">
      <Navbar />

      <main className="pt-28 pb-20">
        <div className="container mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Link
            to={localizedHref("/")}
            className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("legal.backToHome")}
          </Link>

          <h1 className="mb-2 text-4xl font-bold tracking-tight text-foreground">
            {t("legal.terms.title")}
          </h1>
          <p className="mb-12 text-sm text-muted-foreground">
            {t("legal.lastUpdated", { date: lastUpdated })}
          </p>

          {locale !== "en" && (
            <p className="mb-8 text-xs text-muted-foreground italic">{t("legal.disclaimer")}</p>
          )}

          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.agreement.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.agreement.body", {
                  company: companyName,
                })}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.description.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.description.body", {
                  company: companyName,
                })}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.accounts.heading")}
              </h2>
              <p className="mb-3 leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.accounts.intro")}
              </p>
              <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                {accountItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.accounts.outro")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.ip.heading")}
              </h2>

              <h3 className="mt-4 mb-2 text-lg font-semibold text-foreground">
                {t("legal.terms.sections.ip.oursHeading")}
              </h3>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.ip.oursBody", { company: companyName })}
              </p>

              <h3 className="mt-4 mb-2 text-lg font-semibold text-foreground">
                {t("legal.terms.sections.ip.clientHeading")}
              </h3>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.ip.clientBody", {
                  company: companyName,
                })}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.payment.heading")}
              </h2>
              <p className="mb-3 leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.payment.intro")}
              </p>
              <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                {paymentItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.use.heading")}
              </h2>
              <p className="mb-3 leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.use.intro")}
              </p>
              <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                {useItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.confidentiality.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.confidentiality.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.liability.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.liability.body", {
                  company: companyName,
                })}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.warranties.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.warranties.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.indemnification.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.indemnification.body", {
                  company: companyName,
                })}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.termination.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.termination.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.governing.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.governing.body", {
                  company: companyName,
                })}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.changes.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.changes.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.severability.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.severability.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.terms.sections.contact.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.terms.sections.contact.body")}
              </p>
              <div className="mt-3 rounded-lg bg-muted p-4">
                <p className="font-semibold text-foreground">{companyName}</p>
                <p className="text-muted-foreground">
                  {t("legal.contactBlock.emailLabel")}{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="font-medium text-primary hover:text-primary/80"
                  >
                    {contactEmail}
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
